import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Eye, AlertCircle, Check, Sparkles, Loader2, Clock, ThumbsUp, HelpCircle, AlertTriangle,
  Crown, DollarSign, Cpu, Workflow, Scale, ShieldAlert, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Logo from '@/components/Logo';

const ICON_MAP = {
  Crown, DollarSign, Cpu, Workflow, Scale, ShieldAlert, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap,
};

/* -------------------------------------------------------------------------- */
/*  Public read-only Council Observer page                                    */
/*  Route: /observe/council/:councilId?token=...                              */
/*  No auth required — the token IS the authorization.                        */
/*  Auto-polls every 4s while observer_expires_at is in the future and the    */
/*  session looks "live" (rounds change or no synthesis yet).                 */
/* -------------------------------------------------------------------------- */

const POLL_INTERVAL_MS = 4000;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

export default function CouncilObserve() {
  const { councilId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [session, setSession] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastRoundsRef = useRef(0);
  const lastAnswerCountRef = useRef(0);

  const fetchSession = useCallback(async () => {
    if (!token) {
      setError('No observer token provided.');
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(
        `${BACKEND}/api/ai/council/observe/${councilId}`,
        { params: { token }, timeout: 15000 },
      );
      setSession(data);
      // Toast on round changes
      const r = Number(data.rounds || 0);
      const ans = (data.answers || []).length;
      if (lastRoundsRef.current && r > lastRoundsRef.current) {
        toast.info(`New round added — round ${r}`);
      } else if (lastAnswerCountRef.current && ans > lastAnswerCountRef.current && r === lastRoundsRef.current) {
        toast.info(`Voice updated`);
      }
      lastRoundsRef.current = r;
      lastAnswerCountRef.current = ans;
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load this council.');
    } finally { setLoading(false); }
  }, [councilId, token]);

  // Initial load + personas catalog (anonymous endpoint — wrap with try/catch
  // since the persona catalog is auth-gated but we have a public mirror via
  // the observe endpoint that already includes member ids; if catalog fails
  // we just show ids instead of pretty names).
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on polling */
  useEffect(() => {
    fetchSession();
    axios.get(`${BACKEND}/api/ai/council/personas-public`, { timeout: 5000 })
      .then(({ data }) => setPersonas(data || []))
      .catch(() => setPersonas([]));
  }, [fetchSession]);

  // Auto-poll while expiry is in the future and not yet 'closed'
  useEffect(() => {
    if (!session || !session.observer_expires_at) return undefined;
    const expiresMs = session.observer_expires_at * 1000;
    if (Date.now() >= expiresMs) return undefined;
    const id = setInterval(fetchSession, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [session, fetchSession]);
  /* eslint-enable */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white/55 text-xs uppercase tracking-[0.3em]" data-testid="observe-loading">
        <Loader2 size={14} className="animate-spin mr-3" /> Loading deliberation…
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-8">
        <div className="pal-card pal-grid-bg p-8 max-w-md text-center">
          <AlertCircle size={28} className="text-rose-400 mx-auto" />
          <p className="mt-3 text-sm">{error || 'Observer link is invalid or expired.'}</p>
          <p className="mt-2 text-xs text-white/45">Ask the council host for a fresh share link.</p>
        </div>
      </div>
    );
  }

  const personaById = (id) => personas.find((p) => p.id === id);
  const personaName = (id) => personaById(id)?.english_name || id;
  const synth = session.synthesis || {};
  /* eslint-disable -- v6 react-hooks/purity false-positive on Date.now() expiry check */
  const expiresMs = session.observer_expires_at ? session.observer_expires_at * 1000 : 0;
  const expired = expiresMs && Date.now() >= expiresMs;
  /* eslint-enable */

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A]" data-testid="observe-page">
      {/* Top bar */}
      <header className="border-b border-slate-200 dark:border-white/10 bg-white dark:bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
          <Logo />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#8B3FC5]/40 bg-[#FFF8E5] dark:bg-[#3a2f0a] text-[10px] uppercase tracking-[0.22em] font-bold text-[#7A5A00] dark:text-[#F5E1A4]">
            <Eye size={11} /> Observer · read-only
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 lg:px-6 py-8 space-y-4">
        {/* Header */}
        <div className="pal-card pal-grid-bg p-5 lg:p-6">
          <div className="pal-bracket text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
            Council session · {fmtDateTime(session.created_at)}
            {!expired && (
              <span className="inline-flex items-center gap-1 ml-3 text-slate-400 dark:text-white/45 normal-case tracking-normal">
                <Clock size={11} /> live · auto-refreshing
              </span>
            )}
            {expired && (
              <span className="inline-flex items-center gap-1 ml-3 text-rose-500 normal-case tracking-normal">
                link expired
              </span>
            )}
          </div>
          <h1 className="display text-2xl lg:text-3xl mt-2 font-bold leading-snug">{session.question}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
            <div><span className="pal-bracket text-slate-400 dark:text-white/45 mr-2">Moderator</span><b>{personaName(session.moderator)}</b></div>
            <div><span className="pal-bracket text-slate-400 dark:text-white/45 mr-2">Members</span><b>{(session.members || []).length}</b></div>
            <div><span className="pal-bracket text-slate-400 dark:text-white/45 mr-2">Rounds</span><b>{session.rounds || 1}</b></div>
          </div>
        </div>

        {/* Synthesis */}
        <div className="pal-card pal-grid-bg p-5 lg:p-6" data-testid="observe-synthesis">
          <div className="pal-bracket text-[#5E1F8B] dark:text-[#8B3FC5]">
            Synthesis{session.rounds > 1 && <span className="ml-2 text-slate-400 dark:text-white/35 normal-case tracking-normal">· after {session.rounds} rounds</span>}
          </div>
          {synth.position ? (
            <h2 className="display text-xl lg:text-2xl mt-2 font-bold leading-snug">{synth.position}</h2>
          ) : (
            <p className="mt-2 text-sm text-slate-500 dark:text-white/55 italic">Awaiting synthesis…</p>
          )}
          {synth.changes_from_prior && (
            <p className="mt-2 text-xs italic text-slate-500 dark:text-white/55 border-l-2 border-[#8B3FC5]/40 pl-3">
              Changed from prior: {synth.changes_from_prior}
            </p>
          )}
          {Array.isArray(synth.rationale) && synth.rationale.length > 0 && (
            <ul className="mt-4 space-y-2">
              {synth.rationale.map((r, i) => (
                <li key={i} className="text-sm flex gap-2"><span className="text-[#5E1F8B] dark:text-[#8B3FC5] shrink-0">·</span><span>{r}</span></li>
              ))}
            </ul>
          )}
          {Array.isArray(synth.dissents) && synth.dissents.length > 0 && (
            <div className="mt-6">
              <div className="pal-bracket text-rose-600">Dissents</div>
              <div className="mt-2 space-y-2">
                {synth.dissents.map((d, i) => (
                  <div key={i} className="rounded-md bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-400/20 p-3 text-sm">
                    <b>{personaName(d.persona)}:</b> {d.objection}
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(synth.next_actions) && synth.next_actions.length > 0 && (
            <div className="mt-6">
              <div className="pal-bracket text-emerald-600 dark:text-emerald-400">Next actions</div>
              <ul className="mt-2 space-y-1.5">
                {synth.next_actions.map((a, i) => (
                  <li key={i} className="text-sm flex gap-2"><Check size={14} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" /><span>{a}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Transcript */}
        <div>
          <div className="pal-bracket text-slate-500 dark:text-white/55 mb-3">
            {session.rounds > 1 ? `Latest round · ${session.rounds}` : 'Transcript'} · {(session.answers || []).length} voices
          </div>
          <div className="space-y-3">
            {(session.answers || []).map((a, idx) => {
              const p = personaById(a.persona);
              const Icon = p ? (ICON_MAP[p.icon] || Sparkles) : Sparkles;
              return (
                <div key={`${a.persona}-${idx}`} className="pal-card pal-grid-bg p-4 lg:p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#8B3FC5]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold">{personaName(a.persona)}</div>
                      {p?.english_role && <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45 font-bold mt-0.5">{p.english_role}</div>}
                      <p className="mt-2 text-sm text-slate-700 dark:text-white/80 whitespace-pre-wrap leading-relaxed">{a.answer}</p>

                      {/* Reaction strip */}
                      <ReactionStrip
                        councilId={councilId}
                        token={token}
                        persona={a.persona}
                        aggregates={session.reactions?.[a.persona] || { like: 0, question: 0, warn: 0 }}
                        onSubmit={fetchSession}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <footer className="pt-4 pb-8 text-center text-[11px] text-slate-400 dark:text-white/35">
          You&apos;re viewing this as a guest. Refreshes automatically while the link is live.
        </footer>
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ReactionStrip — 👍 / ❓ / ⚠️ buttons on each persona answer                 */
/* -------------------------------------------------------------------------- */
function ReactionStrip({ councilId, token, persona, aggregates, onSubmit }) {
  const [mine, setMine] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`ve_obs_${councilId}_${persona}`)) || {}; }
    catch { return {}; }
  });
  const [showNote, setShowNote] = useState(null);  // reaction key or null
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (reaction, withNote = '') => {
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND}/api/ai/council/observe/${councilId}/react`,
        { persona, reaction, note: withNote || null },
        { params: { token } },
      );
      const next = { ...mine, [reaction]: true };
      setMine(next);
      try { localStorage.setItem(`ve_obs_${councilId}_${persona}`, JSON.stringify(next)); }
      catch { /* private mode */ }
      toast.success(reaction === 'like' ? 'Liked.' : reaction === 'question' ? 'Question flagged.' : 'Warning flagged.');
      setShowNote(null);
      setNote('');
      onSubmit?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record.');
    } finally { setSubmitting(false); }
  };

  const OPTS = [
    { key: 'like',     label: 'Agree',    icon: ThumbsUp,      tone: 'emerald', needsNote: false },
    { key: 'question', label: 'Confused', icon: HelpCircle,    tone: 'amber',   needsNote: true  },
    { key: 'warn',     label: 'Risk',     icon: AlertTriangle, tone: 'rose',    needsNote: true  },
  ];

  const TONE_CLASS = {
    emerald: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
    amber:   'border-amber-200 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20',
    rose:    'border-rose-200 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20',
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-white/5" data-testid={`reaction-strip-${persona}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {OPTS.map((o) => {
          const Icon = o.icon;
          const isMine = mine[o.key];
          const count = aggregates?.[o.key] || 0;
          return (
            <button
              key={o.key}
              onClick={() => {
                if (isMine) return;
                if (o.needsNote) setShowNote(o.key);
                else submit(o.key);
              }}
              disabled={submitting || isMine}
              data-testid={`reaction-${persona}-${o.key}`}
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border transition ${TONE_CLASS[o.tone]} ${isMine ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
              title={isMine ? 'You already reacted' : o.label}
            >
              <Icon size={10} />
              {o.label}
              {count > 0 && <span className="ml-1 opacity-70">· {count}</span>}
            </button>
          );
        })}
        {(aggregates?.like + aggregates?.question + aggregates?.warn) > 0 && (
          <span className="text-[10px] text-slate-400 dark:text-white/35 ml-1">
            from {(aggregates.like || 0) + (aggregates.question || 0) + (aggregates.warn || 0)} observer{(aggregates.like || 0) + (aggregates.question || 0) + (aggregates.warn || 0) === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {showNote && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={showNote === 'warn' ? "What's the risk?" : "What's the confusion?"}
            data-testid={`reaction-${persona}-${showNote}-note`}
            className="flex-1 text-xs px-2 py-1.5 rounded border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 focus:outline-none focus:border-[#8B3FC5]"
            autoFocus
          />
          <button
            onClick={() => submit(showNote, note)}
            disabled={submitting || !note.trim()}
            className="text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1.5 rounded bg-[#8B3FC5] text-white hover:bg-[#AE52EA] disabled:opacity-50"
          >
            Send
          </button>
          <button
            onClick={() => { setShowNote(null); setNote(''); }}
            className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
